/**
 * @file useAuthInit.js
 * @feature Auth
 * @description Orchestrates the one-time auth initialisation on mount:
 *   1. Check if an active account + valid token exist in session
 *   2. Refresh if the token is expired / near-expiry
 *   3. Schedule a proactive refresh timer
 *
 * FDD rationale: "initialise auth state" is a single feature behaviour.
 * Keeps Pages.jsx free of imperative bootstrap code.
 */

import { useEffect, useContext } from 'react';
import { useMsal } from '@azure/msal-react';
import { UserContext } from '../../components/UserContext';
import { AUTH_SCOPES, TOKEN_MIN_VALID_MS } from '../../config/Auth.config';

/**
 * @param {Function} fetchUserProfile
 * @param {Function} refreshToken
 * @param {Function} getTimeUntilRefresh
 * @param {Function} isNearExpiry
 */
export const useAuthInit = (fetchUserProfile, refreshToken, getTimeUntilRefresh, isNearExpiry) => {
  const { instance } = useMsal();
  const { setLoading , } = useContext(UserContext);

  useEffect(() => {
    let refreshTimer;

    const scheduleRefresh = (account) => {
      // If account has no claims, retry in 1 min
      if (!account?.idTokenClaims?.exp) {
        void refreshToken(account);
        refreshTimer = setTimeout(() => scheduleRefresh(account), 60_000);
        return;
      }

      if (isNearExpiry()) void refreshToken(account);

      const delay = getTimeUntilRefresh();
      refreshTimer = setTimeout(async () => {
        const ok = await refreshToken(account);
        const freshAccount = instance.getActiveAccount();
        if (ok && freshAccount) {
          scheduleRefresh(freshAccount);
        } else {
          refreshTimer = setTimeout(() => scheduleRefresh(freshAccount), 60_000);
        }
      }, delay);
    };

    const initAuth = () => {
      const account = instance.getActiveAccount();
      const storedToken = sessionStorage.getItem('id_token');
      const storedProfile = sessionStorage.getItem('userProfile');

      if (account && storedToken) {
        const tokenExp = (account.idTokenClaims?.exp ?? 0) * 1000;
        const isValid = tokenExp > Date.now() + TOKEN_MIN_VALID_MS;

        if (isValid) {
          if (storedProfile) {
            // Hydrate context directly from cache — no network call needed
            // const { setUserProfile } = require('../../components/UserContext');
            setLoading(false);
          } else {
            void fetchUserProfile(storedToken);
          }
        } else {
          void refreshToken(account);
        }

        scheduleRefresh(account);
        return;
      }

      if (account && !storedToken) {
        instance
          .acquireTokenSilent({ scopes: AUTH_SCOPES, account })
          .then(({ idToken }) => {
            sessionStorage.setItem('id_token', idToken);
            void fetchUserProfile(idToken);
            scheduleRefresh(account);
          })
          .catch((err) => {
            if (err.name === 'InteractionRequiredAuthError') instance.loginRedirect();
          });
        return;
      }

      if (!account && storedToken) {
        sessionStorage.removeItem('id_token');
        sessionStorage.removeItem('userProfile');
      }

      setLoading(false);
    };

    initAuth();

    return () => clearTimeout(refreshTimer);
  }, [instance, fetchUserProfile, refreshToken, getTimeUntilRefresh, isNearExpiry, setLoading]);
};